import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { FaExclamationCircle } from 'react-icons/fa';
import styled from 'styled-components';

import { StockStatus } from '~/shared/utils/getStockStatus';
import { mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useGroupSizeSelectorContext } from '../GroupSizeSelectorProvider/GroupSizeSelectorProvider';

const ScarcityNotice = styled.span`
  color: ${({ theme }) => theme.colors.errorMessage};
  margin-left: 6px;
  font-weight: 700;
`;

const SelectorTextWrapper = styled.div`
  color: ${({ theme }) => theme.colors.selectorText};
  margin-bottom: 8px;
`;

const SelectedLabelText = styled.span`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  text-transform: uppercase;
`;

const SelectSizeWarningMessage = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.errorMessage};
  display: flex;
  letter-spacing: ${-0.4 / 16}em;
`;

const ErrorIcon = styled(FaExclamationCircle)`
  font-size: 16px;
  font-weight: 500;
  margin-right: 4px;
`;

const SoldOutMessage = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.errorMessage};
  display: flex;
  font-weight: 400;
`;

const SelectorLabelWrapper = styled.div`
  display: flex;
`;

const SelectorList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  gap: 6px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  ${mobile`
    grid-template-columns: 1fr 1fr 1fr;
    padding: 16px;
  `}
`;

const SelectorRadioButton = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  opacity: 0;
`;

const SelectorItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 40px;
  width: 100%;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.palette.shadow25};
  background-color: ${({ isSoldOut, theme }) =>
    isSoldOut ? theme.colors.palette.shadowGray : theme.colors.palette.white};
  opacity: ${({ isSoldOut }) => (isSoldOut ? 0.7 : 1)};
`;

const SelectorItemLabel = styled.label`
  font-size: 16px;
  text-align: center;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.palette.shadow25};
  background: ${({ isSoldOut, theme }) =>
    isSoldOut
      ? `linear-gradient(
          to top left,
          transparent calc(50% - 1px),
          ${theme.colors.soldOutSelectorItemSizeInput} calc(50% - 1px),
          ${theme.colors.soldOutSelectorItemSizeInput} calc(50% + 1px),
          transparent calc(50% + 1px),
          transparent
        )`
      : 'none'};

  ${SelectorRadioButton}:checked + & {
    border: 2px solid black;
  }
`;

const ScarcityMessage = ({ availableQuantity }) => {
  return (
    <ScarcityNotice>
      {availableQuantity === StockStatus.LOW_STOCK && (
        <FormattedMessage
          id="site_product.scarcity_banner_low_stock"
          defaultMessage="Low Stock"
        />
      )}
      {availableQuantity === StockStatus.CRITICAL && (
        <FormattedMessage
          id="site_product.scarcity_banner_only_n_left"
          defaultMessage="Only {1} left!"
          values={{ 1: availableQuantity }}
        />
      )}
      {availableQuantity === StockStatus.URGENT && (
        <FormattedMessage
          id="site_product.scarcity_banner_urgent_only_n_left"
          defaultMessage="Hurry up! Only {1} left!"
          values={{ 1: availableQuantity }}
        />
      )}
    </ScarcityNotice>
  );
};

ScarcityMessage.propTypes = {
  availableQuantity: PropTypes.number.isRequired,
};

export default function GroupSizeSelector({
  product,
  selectedOption: initialSelectedOption,
}) {
  const [selectedOption, setSelectedOption] = useState(initialSelectedOption);
  const { setSizeSelection } = useGroupSizeSelectorContext();

  const handleSizeChange = (size) => {
    try {
      const selectedSize = product.componentProductAvailableQuantityList.find(
        (item) => item.size === size
      );
      if (selectedSize) {
        const updatedSelection = {
          size: selectedSize.size,
          isAvailable: selectedSize.availableQuantity > 0,
          availableQuantity: selectedSize.availableQuantity,
        };
        setSelectedOption(updatedSelection);
        setSizeSelection(updatedSelection);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err.message);
    }
  };

  return (
    <>
      <SelectorTextWrapper>
        {!selectedOption && (
          <SelectSizeWarningMessage>
            <ErrorIcon />
            <FormattedMessage
              id="site_product_page.select_a_size"
              defaultMessage="Select a size"
            />
          </SelectSizeWarningMessage>
        )}
        {selectedOption && (
          <SelectorLabelWrapper>
            <FormattedMessage
              id="site_product_page.size"
              defaultMessage="Size:"
            />
            &nbsp;
            <SelectedLabelText>{selectedOption.size}</SelectedLabelText>
            <ScarcityMessage
              availableQuantity={selectedOption.availableQuantity}
            />
            {!selectedOption.isAvailable && (
              <SoldOutMessage>
                &nbsp;-&nbsp;
                <FormattedMessage
                  id="site_product_page.out_of_stock"
                  defaultMessage="Out of Stock"
                />
              </SoldOutMessage>
            )}
          </SelectorLabelWrapper>
        )}
      </SelectorTextWrapper>
      <SelectorList>
        {product?.componentProductAvailableQuantityList?.map((item) => {
          const isSoldOut = item.availableQuantity === 0;
          return (
            <SelectorItem
              key={item.size}
              isSoldOut={isSoldOut}
              onClick={() => handleSizeChange(item.size)}
            >
              <SelectorRadioButton
                name="size"
                id={item.size}
                value={item.size}
                checked={selectedOption?.size === item.size}
                onChange={() => handleSizeChange(item.size)}
              />
              <SelectorItemLabel htmlFor={item.size} isSoldOut={isSoldOut}>
                {item.size}
              </SelectorItemLabel>
            </SelectorItem>
          );
        })}
      </SelectorList>
    </>
  );
}

GroupSizeSelector.propTypes = {
  product: PropTypes.object.isRequired,
  selectedOption: PropTypes.shape({
    size: PropTypes.string,
    isAvailable: PropTypes.bool,
    availableQuantity: PropTypes.number,
  }),
};
